import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { colToLabel, colourRefs, quoteSheetName, rowColToA1 } from '@/model/address';
import { getCell, isSystemSheet, sheetById } from '@/model/workbook';
import { defaultAlign, formatValue } from '@/engine/format';
import { measureText } from './measure';
import { Engine } from '@/engine/engine';
import type { Border, BorderEdge, Cell, Sheet } from '@/model/types';
import { HEADER_H, HEADER_W, cellHeight, colAxis, indexAt, offsetAt, rangeRect, rowAxis, sizeAt, windowOf } from './geometry';
import { mergeAt } from '@/model/merge';
import type { Merge } from '@/model/types';
import { parseTsv, toTsv } from './clipboard';
import { parseHtmlTable } from './htmlClipboard';
import { CellEditor } from './CellEditor';
import { FormatMenu } from '@/ui/FormatMenu';
import { isFiltered } from '@/model/filters';
import { colourFor, optionIndex } from '@/model/dropdowns';
import { Popover, rectOf, type AnchorRect } from '@/ui/Popover';
import type { ColumnList } from '@/model/types';
import { DbRowMenu, DbTitleButton } from '@/ui/DbTitleMenu';
import { columnFormat, subRowsOf, type SubRow } from '@/giraffe/database';
import type { Database } from '@/model/types';
import { DEFAULT_ROW_HEIGHT } from '@/model/workbook';

/*
 * chart.js is a large part of the bundle and most sheets have no chart, so it
 * loads on first use rather than holding up the app's first paint.
 */
const ChartLayer = lazy(() => import('@/charts/ChartLayer').then((m) => ({ default: m.ChartLayer })));

/** A database sub-row is an ordinary row's height, so the two read as one table. */
const SUB_ROW_H = DEFAULT_ROW_HEIGHT;

/** Column resize and the fill handle capture on their own elements. */
type DragMode =
  | { kind: 'none' }
  | { kind: 'select' }
  | { kind: 'point' }
  /** Dragging across headers to extend a whole-row/column selection. */
  | { kind: 'band'; axis: 'row' | 'col'; anchor: number }
  /** Dragging a selected band of rows/columns somewhere else. */
  | { kind: 'reorder'; axis: 'row' | 'col'; from: number; count: number };

export function Grid(): JSX.Element {
  useStore();
  const sheet = store.sheet();
  const sel = store.sel;
  const editing = store.editing;
  // Areas/Features and the rest of the system block are dense tables where a
  // long value spilling across a data column would read as broken, so they
  // default to clipped — but an ordinary sheet is still a spreadsheet, and
  // overrunning into an empty neighbour is what every spreadsheet does by
  // default. Either way, an explicit per-cell choice from the Format menu
  // (cell.s.overflow) always wins.
  const defaultOverflow: 'overrun' | 'clip' = isSystemSheet(sheet) ? 'clip' : 'overrun';

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scroll, setScroll] = useState({ x: 0, y: 0 });
  const [viewport, setViewport] = useState({ w: 800, h: 500 });
  const dragRef = useRef<DragMode>({ kind: 'none' });
  /** The rectangle a fill drag would write, shown as an outline while dragging. */
  const [fillPreview, setFillPreview] = useState<{ r1: number; c1: number; r2: number; c2: number } | null>(null);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  /** The right-clicked database sub-row, while its menu is open. */
  const [subMenu, setSubMenu] = useState<{ x: number; y: number; dbId: string; sub: number; label: string } | null>(null);
  /** Where a dragged band of rows/columns would land. */
  const [dropSlot, setDropSlot] = useState<number | null>(null);

  const cols = useMemo(() => colAxis(sheet), [sheet, sheet.cols, store.getSnapshot()]);
  const filtered = store.filteredRows(sheet);
  const merges = sheet.merges ?? [];
  /** Every coordinate a merge covers, anchor included — the main loop skips all of them; the merge draws once, itself, below. */
  const coveredBy = useMemo(() => {
    const m = new Map<string, Merge>();
    for (const mg of merges) {
      for (let r = mg.r1; r <= mg.r2; r++) for (let c = mg.c1; c <= mg.c2; c++) m.set(`${r},${c}`, mg);
    }
    return m;
  }, [sheet, sheet.merges, store.getSnapshot()]);
  /*
   * Database sub-rows: 11.1, 11.2, … hung beneath the database's row. They are
   * part of that row's band on the axis, not rows of their own, so nothing
   * below is renumbered and no reference to it moves.
   */
  const data = store.data();
  const subRows = useMemo(() => {
    const out = new Map<number, { db: Database; rows: SubRow[] }>();
    for (const db of Object.values(sheet.databases ?? {})) {
      if (db.collapsed) continue;
      out.set(db.anchor.row, { db, rows: subRowsOf(db, data, store.dbFilterText(db)) });
    }
    return out;
  }, [sheet, sheet.databases, data, store.getSnapshot()]);
  const below = useCallback((r: number) => (subRows.get(r)?.rows.length ?? 0) * SUB_ROW_H, [subRows]);
  const rows = useMemo(() => rowAxis(sheet, 20, filtered, below), [sheet, sheet.rows, filtered, below, store.getSnapshot()]);
  /** A row's own height, without its sub-rows — what its cells, header and editor use. */
  const cellH = (r: number): number => cellHeight(rows, r, below);

  /*
   * The axis runs past the sheet — spare indices so offsets, fill previews and
   * an overrun off the last column all have somewhere to land — but nothing
   * beyond it is DRAWN. The selection clamps to the sheet, so a rendered row
   * 205 looked like sheet and behaved like nothing: it could not be clicked,
   * and a filter that hid every blank row left it sitting there anyway.
   */
  const win = (w: { first: number; last: number }, limit: number) => ({
    first: Math.min(w.first, limit),
    last: Math.min(w.last, limit),
  });
  /*
   * `scroll` is CANVAS-relative and the canvas now begins with the header
   * gutter, so sheet space starts HEADER_W / HEADER_H in. Everything below the
   * canvas element is still plain sheet coordinates — see `.gt-content`.
   */
  const contentX = Math.max(0, scroll.x - HEADER_W);
  const contentY = Math.max(0, scroll.y - HEADER_H);
  const colWin = win(windowOf(cols, contentX, viewport.w), sheet.cols);
  const rowWin = win(windowOf(rows, contentY, viewport.h), sheet.rows);

  /*
   * Frozen rows and columns.
   *
   * Rather than four scroll panes, a frozen cell keeps its real position and
   * has the scroll offset ADDED back, so it stays put on screen while
   * everything else moves under it. One canvas, one set of coordinates, and the
   * content passing behind the frozen band is what a frozen pane looks like
   * anyway.
   */
  const freezeRows = sheet.freezeRows ?? 0;
  const freezeCols = sheet.freezeCols ?? 0;
  const frozenH = freezeRows > 0 ? offsetAt(rows, freezeRows + 1) : 0;
  const frozenW = freezeCols > 0 ? offsetAt(cols, freezeCols + 1) : 0;
  /*
   * Everything pinned is pinned by `position: sticky`, and nothing by JS.
   *
   * The pins used to be transforms written from the `scroll` event. On macOS
   * that cannot hold still: scrolling is composited off the main thread, so the
   * event arrives AFTER the frame it describes has already been painted, and
   * during a trackpad fling or a rubber-band overscroll the band visibly
   * stretches away from its column and snaps back — elastic, not pinned. No
   * amount of writing the transform earlier fixes it, because the scroll
   * position we are told about is already stale.
   *
   * So the headers moved INSIDE the one scroll container and every pinned thing
   * is sticky: the compositor holds it, exactly, with no main-thread work. Which
   * frees `scroll` state to do the only thing it is still fit for — deciding
   * which rows and columns are worth rendering, where a frame of lag is
   * invisible.
   *
   * The canvas is the header gutter plus the sheet; `.gt-content` is an absolute
   * box inset by the gutter, so every coordinate inside it is plain sheet space,
   * unchanged. A sticky inset of HEADER_W / HEADER_H is therefore "pin against
   * the far edge of the gutter", which is where the frozen band belongs.
   */
  const pinSelX = sel.c2 <= freezeCols;
  const pinSelY = sel.r2 <= freezeRows;
  const canvasW = Math.max(HEADER_W + cols.total, viewport.w);
  const canvasH = Math.max(HEADER_H + rows.total, viewport.h);

  // A frozen band is on screen even when the scroll has left it far behind, so
  // its indices are always in the render set.
  const rowIndices = union(range(1, freezeRows), range(rowWin.first, rowWin.last));
  const colIndices = union(range(1, freezeCols), range(colWin.first, colWin.last));

  // -- viewport measurement -------------------------------------------------
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = (): void => setViewport({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /*
   * Give the keyboard back to the grid when the editor closes.
   *
   * The editor is an <input> that unmounts on commit, and focus then falls to
   * document.body — so the next arrow key scrolled the container instead of
   * moving the selection, and nothing could be typed until the user clicked a
   * cell. The grid body is the keyboard surface; it has to hold focus whenever
   * no editor is open.
   */
  /*
   * An explicit request for the keyboard — adding or switching a sheet.
   *
   * Unconditional, unlike the passive restore below: the request only comes
   * from an action that means "go to the sheet", and at that moment focus is
   * usually sitting on the button that was clicked.
   */
  useEffect(() => {
    if (store.editing) return; // mid-formula: focus belongs to the editor
    bodyRef.current?.focus({ preventScroll: true });
  }, [store.gridFocusToken]);

  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) {
      const el = bodyRef.current;
      // Only steal focus back if it landed nowhere — never out of a toolbar
      // control or a side pane the user has deliberately clicked into.
      const active = document.activeElement;
      if (el && (active === null || active === document.body)) el.focus({ preventScroll: true });
    }
    wasEditing.current = editing !== null;
  }, [editing]);

  // -- keep the active cell in view ----------------------------------------
  useEffect(() => {
    const el = bodyRef.current;
    // While editing, follow the POINTED cell instead — arrowing to a reference
    // off the bottom of the sheet has to scroll to it like any other selection.
    const point = editing?.pointing;
    if (!el || (editing && !point)) return;
    const targetRow = point ? point.r2 : sel.row;
    const targetCol = point ? point.c2 : sel.col;
    const x = offsetAt(cols, targetCol);
    const w = sizeAt(cols, targetCol);
    const y = offsetAt(rows, targetRow);
    const h = cellH(targetRow);
    let nextLeft = el.scrollLeft;
    let nextTop = el.scrollTop;
    // A frozen band covers the near edge of the viewport, so scrolling a cell
    // "just into view" would slide it underneath. The usable edge starts after
    // the band — unless the cell IS in the band, where it is already on screen.
    // In CANVAS coordinates, since that is what scrollLeft/scrollTop are: the
    // gutter is the first band the cell has to clear, and the frozen band the
    // second.
    const cx = HEADER_W + x;
    const cy = HEADER_H + y;
    const padX = HEADER_W + (targetCol > freezeCols ? frozenW : 0);
    const padY = HEADER_H + (targetRow > freezeRows ? frozenH : 0);
    if (cx - padX < el.scrollLeft) nextLeft = Math.max(0, cx - padX);
    else if (cx + w > el.scrollLeft + el.clientWidth) nextLeft = cx + w - el.clientWidth;
    if (cy - padY < el.scrollTop) nextTop = Math.max(0, cy - padY);
    else if (cy + h > el.scrollTop + el.clientHeight) nextTop = cy + h - el.clientHeight;
    if (nextLeft !== el.scrollLeft || nextTop !== el.scrollTop) {
      el.scrollLeft = nextLeft;
      el.scrollTop = nextTop;
    }
  }, [sel.row, sel.col, editing, cols, rows, freezeRows, freezeCols, frozenH, frozenW]);

  /*
   * Where the editor sits on SCREEN, remembered from its home sheet.
   *
   * Crossing to another sheet must not move the box: the user is mid-formula
   * and looking at it, and a box that jumps to a corner is a box they have to
   * find again. This records the last position it held at home; the away render
   * adds the away sheet's scroll back so the same screen spot is used.
   */
  const editorAnchor = useRef({ x: 10, y: 10, w: 160, h: 24 });
  useLayoutEffect(() => {
    if (!editing || editing.sheetId !== sheet.id) return;
    editorAnchor.current = {
      x: Math.max(0, offsetAt(cols, editing.col) - contentX),
      y: Math.max(0, offsetAt(rows, editing.row) - contentY),
      w: sizeAt(cols, editing.col),
      h: cellH(editing.row),
    };
  });

  const onScroll = useCallback((): void => {
    const el = bodyRef.current;
    if (!el) return;
    // Only the render window. The pinning is the compositor's, via sticky.
    setScroll({ x: el.scrollLeft, y: el.scrollTop });
  }, []);

  // -- pointer: selection drag & resize ------------------------------------
  // Pointer CAPTURE, not window listeners. Inside a cross-origin iframe a
  // window-level pointermove never arrives, so the canonical "mousedown then
  // listen on window" recipe sticks on the first pixel. (sdk-pitfalls.md 8)
  /*
   * Where the pointer is in SHEET space.
   *
   * A frozen band does not scroll, so a point inside one cannot be un-scrolled
   * like the rest: the band always shows columns 1..freezeCols starting at the
   * far edge of the gutter, whatever `scrollLeft` says. Without this, clicking a
   * frozen cell on a scrolled sheet selected whichever column happened to be
   * passing behind it — A19 came back as F19 — which has been true since freeze
   * shipped, because the hit test knew nothing about it.
   */
  const sheetPoint = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const el = bodyRef.current!;
      const rect = el.getBoundingClientRect();
      const vx = clientX - rect.left;
      const vy = clientY - rect.top;
      // The gutter is part of the scrolled canvas, so sheet space starts after
      // it. A point IN the gutter reads as row/column 1, which is what the
      // headers' own handlers want anyway.
      const inColBand = freezeCols > 0 && vx < HEADER_W + frozenW;
      const inRowBand = freezeRows > 0 && vy < HEADER_H + frozenH;
      return {
        x: vx - HEADER_W + (inColBand ? 0 : el.scrollLeft),
        y: vy - HEADER_H + (inRowBand ? 0 : el.scrollTop),
      };
    },
    [freezeCols, freezeRows, frozenW, frozenH]
  );

  const cellAt = useCallback(
    (clientX: number, clientY: number): { row: number; col: number } => {
      const { x, y } = sheetPoint(clientX, clientY);
      return { row: indexAt(rows, y), col: indexAt(cols, x) };
    },
    [cols, rows, sheetPoint]
  );

  /*
   * The editor lives INSIDE the scrolling body, so every click on it reaches
   * the body's handlers too.
   *
   * Clicking into the formula to move the caret was therefore read as clicking
   * a cell: mid-formula that inserts a reference, otherwise it commits, and
   * either way the cell went back to showing its value. A click that started in
   * the editor is the editor's.
   */
  const inEditor = (e: React.SyntheticEvent): boolean =>
    e.target instanceof Element && e.target.closest('.gt-editor') !== null;

  const onBodyPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0 || inEditor(e)) return;
    const { row, col } = cellAt(e.clientX, e.clientY);

    // Mid-formula, clicking a cell inserts its reference rather than ending the
    // edit — the other half of arrow-key pointing, and how anyone actually
    // builds `=B4*C4` without typing addresses.
    if (editing && store.canPoint()) {
      e.preventDefault();
      store.pointAt(row, col, e.shiftKey);
      dragRef.current = { kind: 'point' };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best-effort */
      }
      return;
    }

    if (editing) store.commitEdit(undefined, 'none');
    store.select(row, col, e.shiftKey);
    dragRef.current = { kind: 'select' };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort; a failed capture just means no drag-select */
    }
    bodyRef.current?.focus();
  };

  const onBodyPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragRef.current.kind === 'point') {
      const { row, col } = cellAt(e.clientX, e.clientY);
      store.pointAt(row, col, true);
      return;
    }
    if (dragRef.current.kind !== 'select') return;
    const { row, col } = cellAt(e.clientX, e.clientY);
    store.select(row, col, true);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragRef.current.kind === 'none') return;
    dragRef.current = { kind: 'none' };
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  // Double-clicking IN the editor selects a word, as it does in any text box.
  const onBodyDoubleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (inEditor(e)) return;
    store.beginEdit();
  };

  /** Which sub-row a sheet-space y falls in, under this row — or null for the row itself. */
  const subRowAt = (row: number, y: number): { dbId: string; sub: number; label: string } | null => {
    const hung = subRows.get(row);
    if (!hung) return null;
    const top = offsetAt(rows, row) + cellH(row);
    if (y < top) return null;
    const sub = Math.floor((y - top) / SUB_ROW_H);
    if (sub < 0 || sub >= hung.rows.length) return null;
    return { dbId: hung.db.id, sub, label: `${row}.${sub + 1}` };
  };

  const onContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    // In the editor, the browser's own text menu is the useful one.
    if (inEditor(e)) return;
    e.preventDefault();
    const { row, col } = cellAt(e.clientX, e.clientY);
    // A database sub-row is not a cell — it is an object (or a group of them)
    // on the map, so its menu is about those objects.
    const sub = subRowAt(row, sheetPoint(e.clientX, e.clientY).y);
    if (sub) {
      if (editing) store.commitEdit(undefined, 'none');
      setSubMenu({ x: e.clientX, y: e.clientY, ...sub });
      return;
    }
    // Right-clicking inside an existing selection keeps it — that is how you
    // format a whole range. Right-clicking outside it moves there first.
    const inside = row >= sel.r1 && row <= sel.r2 && col >= sel.c1 && col <= sel.c2;
    if (!inside) store.select(row, col);
    if (editing) store.commitEdit(undefined, 'none');
    setMenuAt({ x: e.clientX, y: e.clientY });
  };

  /**
   * The fill handle. Pointer CAPTURE on the handle, not window listeners —
   * inside a cross-origin iframe a window-level pointermove never arrives, so
   * the usual recipe sticks on the first pixel.
   *
   * The drag is constrained to one axis: whichever of the two the pointer has
   * travelled further along. A free-form rectangle is almost never what someone
   * dragging a fill handle means, and it makes an overshoot destructive.
   *
   * The source continues as a SERIES where one can be read (1,2,3 -> 4,5,6;
   * monthly dates -> the next month); hold Ctrl/Cmd/Alt on release to repeat it
   * instead.
   */
  const onFillPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    const origin = { ...store.sel };
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* best effort */
    }

    let last: { row: number; col: number } | null = null;

    const onMove = (ev: PointerEvent): void => {
      const { row, col } = cellAt(ev.clientX, ev.clientY);
      const dRow = row < origin.r1 ? origin.r1 - row : row > origin.r2 ? row - origin.r2 : 0;
      const dCol = col < origin.c1 ? origin.c1 - col : col > origin.c2 ? col - origin.c2 : 0;
      const target = dRow >= dCol ? { row, col: origin.c2 } : { row: origin.r2, col };
      last = target;
      setFillPreview({
        r1: Math.min(origin.r1, target.row),
        c1: Math.min(origin.c1, target.col),
        r2: Math.max(origin.r2, target.row),
        c2: Math.max(origin.c2, target.col),
      });
    };

    const onUp = (ev: PointerEvent): void => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      try {
        handle.releasePointerCapture(ev.pointerId);
      } catch {
        /* already released */
      }
      setFillPreview(null);
      // Holding a modifier forces a repeat instead of continuing the series —
      // the escape hatch for "I really do want 1,2,3,1,2,3".
      if (last) store.fillTo(last, ev.ctrlKey || ev.metaKey || ev.altKey);
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };

  // -- keyboard -------------------------------------------------------------
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    /*
     * Mid-formula on ANOTHER sheet, the grid holds the keyboard.
     *
     * The editor only renders on its own sheet, so once the user has clicked a
     * tab to pick a cross-sheet reference there is no input to receive Enter —
     * and it did nothing at all. The grid takes the editor's keys until the
     * formula is finished.
     */
    if (editing && editing.sheetId !== sheet.id) {
      if (e.key === 'Enter') {
        e.preventDefault();
        store.commitEdit(undefined, e.shiftKey ? 'none' : 'down');
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        store.cancelEdit();
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        store.commitEdit(undefined, 'right');
        return;
      }
      if (
        (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
        store.canPoint()
      ) {
        e.preventDefault();
        const [dr, dc] = {
          ArrowUp: [-1, 0],
          ArrowDown: [1, 0],
          ArrowLeft: [0, -1],
          ArrowRight: [0, 1],
        }[e.key] as [number, number];
        store.movePoint(dr, dc, e.shiftKey);
        return;
      }
      return;
    }
    if (editing) return;
    const mod = e.metaKey || e.ctrlKey;

    if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) {
      e.preventDefault();
      store.undo();
      return;
    }
    if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
      e.preventDefault();
      store.redo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      store.fillDown();
      return;
    }
    if (mod && e.key.toLowerCase() === 'r') {
      e.preventDefault();
      store.fillRight();
      return;
    }
    // Cmd+\ on a Mac, Ctrl+\ on Windows — the Google Sheets binding. `code`
    // as well as `key`, because on some layouts the backslash key types
    // something else.
    if (mod && (e.key === '\\' || e.code === 'Backslash')) {
      e.preventDefault();
      store.clearFormatting();
      return;
    }
    if (mod && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      store.applyStyle({ bold: !store.selectionHas('bold') });
      return;
    }
    if (mod && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      store.selectRange(1, 1, sheet.rows, sheet.cols);
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight': {
        e.preventDefault();
        const d = {
          ArrowUp: [-1, 0],
          ArrowDown: [1, 0],
          ArrowLeft: [0, -1],
          ArrowRight: [0, 1],
        }[e.key] as [number, number];
        if (mod) store.jump(d[0], d[1], e.shiftKey);
        else store.move(d[0], d[1], e.shiftKey);
        return;
      }
      case 'Tab':
        e.preventDefault();
        store.move(0, e.shiftKey ? -1 : 1);
        return;
      case 'Enter':
        e.preventDefault();
        store.beginEdit();
        return;
      case 'F2':
        e.preventDefault();
        store.beginEdit();
        return;
      case 'Escape':
        e.preventDefault();
        store.select(sel.row, sel.col);
        return;
      case 'Delete':
      case 'Backspace':
        e.preventDefault();
        store.clearSelection();
        return;
      case 'Home':
        e.preventDefault();
        store.select(mod ? 1 : sel.row, 1, e.shiftKey);
        return;
      case 'PageDown':
        e.preventDefault();
        store.move(Math.floor(viewport.h / 24), 0, e.shiftKey);
        return;
      case 'PageUp':
        e.preventDefault();
        store.move(-Math.floor(viewport.h / 24), 0, e.shiftKey);
        return;
      default:
        break;
    }

    // Typing replaces the cell.
    if (!mod && e.key.length === 1 && !e.altKey) {
      e.preventDefault();
      store.beginEdit(e.key);
    }
  };

  // -- clipboard ------------------------------------------------------------
  const onCopy = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing) return;
    e.preventDefault();
    const payload = store.copySelection(false);
    // Displayed text, not raw: that is what Excel and Sheets put on the
    // clipboard, and what they expect to receive.
    e.clipboardData.setData('text/plain', toTsv(payload.display));
  };

  const onCut = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing) return;
    e.preventDefault();
    const payload = store.copySelection(true);
    e.clipboardData.setData('text/plain', toTsv(payload.display));
    store.clearSelection();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing) return;
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    // When the clipboard still holds our own copy, paste the RAW cells so
    // formulas and formats survive; otherwise all we have is the text.
    const internal = store.internalPayloadFor(text);
    if (internal) {
      store.pasteCells(internal.cells, internal.origin, internal);
      return;
    }
    // A foreign paste never carries formulas (no browser exposes Excel's
    // formula-bearing clipboard formats to a web page), but Excel and Sheets
    // both also put an HTML table of the same range on the clipboard, and
    // that table DOES carry formatting — fill, bold, colour, borders — that
    // the plain-text TSV throws away.
    const html = e.clipboardData.getData('text/html');
    const parsed = html ? parseHtmlTable(html) : null;
    if (parsed) store.pasteCells(parsed.values, null, { formats: parsed.formats, styles: parsed.styles, merges: parsed.merges });
    else store.pasteCells(parseTsv(text), null);
  };

  // -- render ---------------------------------------------------------------
  /**
   * Header pointer handling, shared by rows and columns.
   *
   * Shift extends from the anchor; a plain press selects one; dragging from
   * INSIDE an existing multi-selection moves that band, and dragging from
   * outside it extends a new one. That split is what makes a header both a
   * selector and a drag handle without a modifier.
   */
  const onHeaderPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    axis: 'row' | 'col',
    index: number
  ): void => {
    if (e.button !== 0) return;
    e.preventDefault();
    // Without this, the SAME pointerdown bubbles to `.gt-body`'s
    // `onBodyPointerDown` (the header strips are its children, not a
    // sibling) — `cellAt` reads a point in the gutter as row/column 1 (see
    // `sheetPoint`'s own comment), so the bubbled handler would immediately
    // overwrite the just-set whole-row/column band with a single cell at
    // row 1 (for a column header) or column 1 (for a row header).
    // `onHeaderContextMenu` already does this for the same reason.
    e.stopPropagation();
    if (editing) store.commitEdit(undefined, 'none');

    const band = store.selectedBand();
    const inside = band?.axis === axis && index >= band.from && index <= band.to;

    if (e.shiftKey && band?.axis === axis) {
      if (axis === 'row') store.selectRows(band.from, index);
      else store.selectCols(band.from, index);
    } else if (!inside) {
      if (axis === 'row') store.selectRows(index, index);
      else store.selectCols(index, index);
    }

    const current = store.selectedBand()!;
    dragRef.current =
      inside && current.count >= 1
        ? { kind: 'reorder', axis, from: current.from, count: current.count }
        : { kind: 'band', axis, anchor: e.shiftKey ? current.from : index };

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
    bodyRef.current?.focus();
  };

  const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag.kind === 'band') {
      const at = headerIndexAt(drag.axis, e.clientX, e.clientY);
      if (drag.axis === 'row') store.selectRows(drag.anchor, at);
      else store.selectCols(drag.anchor, at);
      return;
    }
    if (drag.kind === 'reorder') {
      setDropSlot(headerSlotAt(drag.axis, e.clientX, e.clientY));
    }
  };

  const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag.kind === 'reorder' && dropSlot !== null) {
      store.moveBand(drag.axis, drag.from, drag.count, dropSlot);
    }
    setDropSlot(null);
    dragRef.current = { kind: 'none' };
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  /** Which row/column the pointer is over. */
  const headerIndexAt = (axis: 'row' | 'col', clientX: number, clientY: number): number => {
    const p = sheetPoint(clientX, clientY);
    return axis === 'row' ? indexAt(rows, p.y) : indexAt(cols, p.x);
  };

  /** The insertion slot — the gap before a row/column, so the last gap exists. */
  const headerSlotAt = (axis: 'row' | 'col', clientX: number, clientY: number): number => {
    const axisObj = axis === 'row' ? rows : cols;
    const p = sheetPoint(clientX, clientY);
    const px = axis === 'row' ? p.y : p.x;
    const i = indexAt(axisObj, px);
    const middle = offsetAt(axisObj, i) + sizeAt(axisObj, i) / 2;
    return px > middle ? i + 1 : i;
  };

  const onHeaderContextMenu = (
    e: React.MouseEvent<HTMLDivElement>,
    axis: 'row' | 'col',
    index: number
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    const band = store.selectedBand();
    const inside = band?.axis === axis && index >= band.from && index <= band.to;
    // Right-clicking outside the selection moves to what was clicked, so the
    // menu always acts on what the user is pointing at.
    if (!inside) {
      if (axis === 'row') store.selectRows(index, index);
      else store.selectCols(index, index);
    }
    setMenuAt({ x: e.clientX, y: e.clientY });
  };

  const headerCols: JSX.Element[] = [];
  const headerColsFrozen: JSX.Element[] = [];
  for (const c of colIndices) {
    const selected = c >= sel.c1 && c <= sel.c2;
    if (sheet.hiddenCols?.[c]) continue;
    // A gap with nothing to say is a gap nobody can undo. The neighbour marks it.
    const hiddenBefore = sheet.hiddenCols?.[c - 1] === true;
    (c <= freezeCols ? headerColsFrozen : headerCols).push(
      <div
        key={c}
        className={`gt-colhead${selected ? ' is-sel' : ''}${hiddenBefore ? ' has-hidden' : ''}${c <= freezeCols ? ' is-frozen' : ''}${isFiltered(sheet, c) ? ' is-filtered' : ''}`}
        style={{ left: offsetAt(cols, c), width: sizeAt(cols, c) }}
        onPointerDown={(e) => onHeaderPointerDown(e, 'col', c)}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        onContextMenu={(e) => onHeaderContextMenu(e, 'col', c)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          store.autoSize('col', c);
        }}
        title="Click to select, drag to move, right-click for insert and delete. Double-click the edge to fit."
      >
        <span>{colToLabel(c)}</span>
        {isFiltered(sheet, c) ? <span className="gt-filter-mark" title="Filtered — right-click to change it">▼</span> : null}
        <div
          className="gt-col-resize"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const handle = e.currentTarget;
            const startX = e.clientX;
            const startW = sizeAt(cols, c);
            try {
              handle.setPointerCapture(e.pointerId);
            } catch {
              /* best effort */
            }
            const onMove = (ev: PointerEvent): void => store.setColWidth(c, startW + (ev.clientX - startX));
            const onUp = (ev: PointerEvent): void => {
              handle.removeEventListener('pointermove', onMove);
              handle.removeEventListener('pointerup', onUp);
              handle.removeEventListener('pointercancel', onUp);
              try {
                handle.releasePointerCapture(ev.pointerId);
              } catch {
                /* already released */
              }
            };
            handle.addEventListener('pointermove', onMove);
            handle.addEventListener('pointerup', onUp);
            handle.addEventListener('pointercancel', onUp);
          }}
        />
      </div>
    );
  }

  /** Rows that hold a database — collapsed or not — for the ▸/▾ in the gutter. */
  const dbRows = new Map(Object.values(sheet.databases ?? {}).map((db) => [db.anchor.row, db] as const));
  const headerRows: JSX.Element[] = [];
  const headerRowsFrozen: JSX.Element[] = [];
  for (const r of rowIndices) {
    const selected = r >= sel.r1 && r <= sel.r2;
    if (sheet.hiddenRows?.[r] || filtered.has(r)) continue;
    const hiddenAbove = sheet.hiddenRows?.[r - 1] === true || filtered.has(r - 1);
    (r <= freezeRows ? headerRowsFrozen : headerRows).push(
      <div
        key={r}
        className={`gt-rowhead${selected ? ' is-sel' : ''}${hiddenAbove ? ' has-hidden' : ''}${r <= freezeRows ? ' is-frozen' : ''}`}
        style={{ top: offsetAt(rows, r), height: cellH(r) }}
        onPointerDown={(e) => onHeaderPointerDown(e, 'row', r)}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        onContextMenu={(e) => onHeaderContextMenu(e, 'row', r)}
        title="Click to select, drag to move, right-click for insert and delete."
      >
        {dbRows.has(r) ? <DbToggle db={dbRows.get(r)!} /> : null}
        {r}
        <div
          className="gt-row-resize"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const handle = e.currentTarget;
            const startY = e.clientY;
            const startH = cellH(r);
            try {
              handle.setPointerCapture(e.pointerId);
            } catch {
              /* best effort */
            }
            const onMove = (ev: PointerEvent): void => store.setRowHeight(r, startH + (ev.clientY - startY));
            const onUp = (ev: PointerEvent): void => {
              handle.removeEventListener('pointermove', onMove);
              handle.removeEventListener('pointerup', onUp);
              handle.removeEventListener('pointercancel', onUp);
              try {
                handle.releasePointerCapture(ev.pointerId);
              } catch {
                /* already released */
              }
            };
            handle.addEventListener('pointermove', onMove);
            handle.addEventListener('pointerup', onUp);
            handle.addEventListener('pointercancel', onUp);
          }}
        />
      </div>
    );
  }

  // Gridlines are drawn as elements, not a CSS background, because a repeating
  // background can only be a fixed pitch — the moment a user resizes one column
  // every line downstream of it is wrong.
  const lines: JSX.Element[] = [];
  for (let c = colWin.first; c <= colWin.last + 1; c++) {
    if (sheet.hiddenCols?.[c]) continue;
    // A merge erases the gridlines that would have crossed through its own
    // INTERIOR — not the one on its outer edge, which is still the boundary
    // with whatever sits outside it. `c` is interior to a merge when it falls
    // strictly after its left edge and no further than its right one.
    const skip: [number, number][] = [];
    for (const m of merges) {
      if (c > m.c1 && c <= m.c2) skip.push([offsetAt(rows, m.r1), offsetAt(rows, m.r2) + cellH(m.r2)]);
    }
    for (const [top, bottom] of splitExcluding(rows.total, skip)) {
      lines.push(
        <div key={`v${c}.${top}`} className="gt-vline" style={{ left: offsetAt(cols, c), top, height: bottom - top }} />
      );
    }
  }
  for (let r = rowWin.first; r <= rowWin.last + 1; r++) {
    if (sheet.hiddenRows?.[r] || filtered.has(r)) continue;
    const skip: [number, number][] = [];
    for (const m of merges) {
      if (r > m.r1 && r <= m.r2) skip.push([offsetAt(cols, m.c1), offsetAt(cols, m.c2) + sizeAt(cols, m.c2)]);
    }
    for (const [left, right] of splitExcluding(cols.total, skip)) {
      lines.push(
        <div key={`h${r}.${left}`} className="gt-hline" style={{ top: offsetAt(rows, r), left, width: right - left }} />
      );
    }
  }

  /*
   * The two bands and their corner are three stacking levels, not one.
   *
   * A cell in the frozen COLUMN scrolls up under the frozen ROWS, so the row
   * band has to cover it — one shared level would let whichever came last in
   * the DOM win, which is how row 19 ended up painted over row 1. Each level
   * carries its own backdrop (empty cells render nothing, so without one the
   * sheet shows through the gaps) and its own gridlines (the backdrop hides the
   * ones underneath).
   */
  const colBand: JSX.Element[] = [];
  const rowBand: JSX.Element[] = [];
  const cornerBand: JSX.Element[] = [];
  /*
   * Every coordinate below is a plain canvas coordinate. The band's WRAPPER
   * carries the scroll offset, applied straight to the DOM on the scroll event,
   * which is what makes the band hold still instead of catching up a frame
   * later. The perpendicular lines have to be redrawn per band because each
   * band's backdrop covers the ones underneath it.
   */
  const vline = (into: JSX.Element[], key: string, cls: string, c: number, top: number, height: number): void => {
    if (sheet.hiddenCols?.[c]) return;
    into.push(<div key={key} className={`gt-vline ${cls}`} style={{ left: offsetAt(cols, c), top, height }} />);
  };
  const hline = (into: JSX.Element[], key: string, cls: string, r: number, left: number, width: number): void => {
    if (sheet.hiddenRows?.[r]) return;
    into.push(<div key={key} className={`gt-hline ${cls}`} style={{ top: offsetAt(rows, r), left, width }} />);
  };

  if (freezeCols > 0) {
    colBand.push(<div key="shade" className="gt-freeze-shade in-col" style={{ width: frozenW, height: rows.total }} />);
    for (let c = 1; c <= freezeCols + 1; c++) vline(colBand, `cv${c}`, 'in-col', c, 0, rows.total);
    for (let r = rowWin.first; r <= rowWin.last + 1; r++) hline(colBand, `ch${r}`, 'in-col', r, 0, frozenW);
    colBand.push(<div key="edge" className="gt-freeze-line is-col" style={{ left: frozenW, height: rows.total }} />);
  }
  if (freezeRows > 0) {
    rowBand.push(<div key="shade" className="gt-freeze-shade in-row" style={{ width: cols.total, height: frozenH }} />);
    for (let r = 1; r <= freezeRows + 1; r++) hline(rowBand, `rh${r}`, 'in-row', r, 0, cols.total);
    for (let c = colWin.first; c <= colWin.last + 1; c++) vline(rowBand, `rv${c}`, 'in-row', c, 0, frozenH);
    rowBand.push(<div key="edge" className="gt-freeze-line is-row" style={{ top: frozenH, width: cols.total }} />);
  }
  if (freezeRows > 0 && freezeCols > 0) {
    cornerBand.push(<div key="shade" className="gt-freeze-shade in-corner" style={{ width: frozenW, height: frozenH }} />);
    for (let c = 1; c <= freezeCols + 1; c++) vline(cornerBand, `kv${c}`, 'in-corner', c, 0, frozenH);
    for (let r = 1; r <= freezeRows + 1; r++) hline(cornerBand, `kh${r}`, 'in-corner', r, 0, frozenW);
  }

  const cells: JSX.Element[] = [];
  // A database's row is tinted the width of the sheet, under its cells, so the
  // table reads as a band and not as a few coloured cells.
  for (const [r] of dbRows) {
    if (!rowIndices.includes(r) || sheet.hiddenRows?.[r] || filtered.has(r)) continue;
    const top = offsetAt(rows, r);
    const inRow = r <= freezeRows;
    (inRow ? rowBand : cells).push(<div key={`dg${r}`} className="gt-dbrow-ground" style={{ top, width: cols.total, height: cellH(r) }} />);
    if (freezeCols > 0) (inRow ? cornerBand : colBand).push(<div key={`dcg${r}`} className="gt-dbrow-ground" style={{ top, width: frozenW, height: cellH(r) }} />);
  }
  /*
   * Merged cells draw once here, spanning their own box — never per-(r,c) in
   * the main loop below, which skips every coordinate a merge covers.
   *
   * offsetAt/sizeAt (via rangeRect) are window-independent — they read off
   * the full cumulative-offset array regardless of what rowIndices/colIndices
   * currently virtualise into view — so a merge whose anchor has scrolled out
   * of that window still gets the right pixel box with no special-casing.
   * Only a cheap index-range overlap test below decides whether to draw it at
   * all, the same cost as the dbRows/subRows visibility checks already here.
   */
  for (const m of merges) {
    if (m.r1 === m.r2 && m.c1 === m.c2) continue; // degenerate, ignore
    const rowsVisible = m.r1 <= rowWin.last && m.r2 >= rowWin.first;
    const colsVisible = m.c1 <= colWin.last && m.c2 >= colWin.first;
    const frozenOverlap = m.r1 <= freezeRows || m.c1 <= freezeCols;
    if (!rowsVisible && !colsVisible && !frozenOverlap) continue;
    if (sheet.hiddenRows?.[m.r1] || sheet.hiddenCols?.[m.c1] || filtered.has(m.r1)) continue;
    const r = m.r1;
    const c = m.c1;
    const cell = getCell(sheet, r, c);
    const list = store.listForCell(r, c);
    const locked = list !== undefined && store.isCellLocked(sheet, c);
    const inRowM = r <= freezeRows;
    const inColM = c <= freezeCols;
    const bandM = inRowM && inColM ? 'corner' : inRowM ? 'row' : inColM ? 'col' : 'none';
    const intoM = bandM === 'corner' ? cornerBand : bandM === 'row' ? rowBand : bandM === 'col' ? colBand : cells;
    const box = rangeRect(cols, rows, cellH, m.r1, m.c1, m.r2, m.c2);
    const b = cellBorderBox(sheet, m.r1, m.c1, m.r2, m.c2, box.x, box.y, box.w, box.h);
    if (b) {
      intoM.push(
        <div
          key={`mb${r},${c}`}
          className="gt-cell-border"
          style={{ left: b.box.x, top: b.box.y, width: b.box.w, height: b.box.h, boxShadow: borderShadow(b.edges) }}
        />
      );
    }
    const value = store.engine.valueAt(sheet, r, c);
    const text = formatValue(value ?? null, cell?.fmt);
    const titleHit = store.dbCellAt(r, c);
    const dbTitle = titleHit ? { dbId: titleHit.db.id, index: titleHit.index } : undefined;
    // A number too wide for the merge's own box still reads as a number,
    // tooltip carrying the full value — same rule as an ordinary cell.
    const overflowedM = typeof value === 'number' && text !== '' && measureText(text, cell?.s?.bold === true) + 10 > box.w;
    intoM.push(
      <CellView
        key={`${r},${c}`}
        cell={cell}
        value={value}
        text={text}
        tooShort={overflowedM}
        warn={store.cellWarning(sheet.id, r, c)}
        match={store.isMatch(sheet.id, r, c)}
        band={bandM}
        list={list}
        onChoose={(option) => store.chooseOption(r, c, option)}
        locked={locked}
        dbTitle={dbTitle}
        onColour={(hex) => store.setCellRaw(r, c, hex)}
        defaultOverflow={defaultOverflow}
        x={box.x}
        y={box.y}
        w={box.w}
        h={box.h}
      />
    );
  }
  for (const r of rowIndices) {
    for (const c of colIndices) {
      // A hidden row or column renders NOTHING. Zero width alone is not enough:
      // overrunning text has `overflow: visible`, so it paints straight out of a
      // box with no width and a hidden column still shows its contents.
      if (sheet.hiddenRows?.[r] || filtered.has(r) || sheet.hiddenCols?.[c]) continue;
      // A merge draws once, as its own box, in the dedicated pass above — not
      // per-(r,c) here, anchor included, or it would draw twice.
      if (coveredBy.has(`${r},${c}`)) continue;
      const cell = getCell(sheet, r, c);
      /*
       * An empty cell normally draws nothing — there are a hundred thousand of
       * them and none has anything to say.
       *
       * One in a dropdown band does: the ▾ that opens the list IS the only sign
       * the band offers one, and a column bound to a project field is empty by
       * definition until somebody picks something. Without this the feature
       * only worked on cells that already had a value in them.
       */
      const list = store.listForCell(r, c);
      if (!cell && !list) continue;
      const locked = list !== undefined && store.isCellLocked(sheet, c);
      // Which stacking level this cell belongs to, and the wrapper that pins it.
      const inRow = r <= freezeRows;
      const inCol = c <= freezeCols;
      const band = inRow && inCol ? 'corner' : inRow ? 'row' : inCol ? 'col' : 'none';
      const into = band === 'corner' ? cornerBand : band === 'row' ? rowBand : band === 'col' ? colBand : cells;

      /*
       * A border is its own element, above the gridlines.
       *
       * Drawn as an inset shadow on the cell it was a layer BELOW them, so the
       * grey line crossing each row boundary cut a gap out of it and a border
       * meant to run down a column arrived dashed. It also takes the cell's OWN
       * box rather than the overrunning one: the border belongs to the cell,
       * not to text that happens to spill past it.
       */
      const b = cellBorderBox(sheet, r, c, r, c, offsetAt(cols, c), offsetAt(rows, r), sizeAt(cols, c), cellH(r));
      if (b) {
        into.push(
          <div
            key={`b${r},${c}`}
            className="gt-cell-border"
            style={{
              left: b.box.x,
              top: b.box.y,
              width: b.box.w,
              height: b.box.h,
              boxShadow: borderShadow(b.edges),
            }}
          />
        );
      }
      const value = store.engine.valueAt(sheet, r, c);
      const text = formatValue(value ?? null, cell?.fmt);
      const own = sizeAt(cols, c);
      const titleHit = store.dbCellAt(r, c);
      const dbTitle = titleHit ? { dbId: titleHit.db.id, index: titleHit.index } : undefined;
      const bold = cell?.s?.bold === true;
      const mode = cell?.s?.overflow ?? defaultOverflow;

      /*
       * How wide this cell gets to draw.
       *
       * Set to "overrun" (not the default — see above), text spills into the
       * empty cells beside it and stops at the first neighbour with something
       * in it. "Overrun" means the BOX gets wider, not that the text escapes:
       * the cell always clips at its own edge, so a blocked neighbour simply
       * means no extra width and the text is cut off there. A number does not
       * overrun at all — it clips, with its full value in the tooltip.
       */
      let width = own;
      let overflowed = false;
      if (mode === 'overrun' && !dbTitle && typeof value === 'string' && text !== '') {
        const needed = measureText(text, bold) + 12;
        if (needed > own) {
          const align = cell?.s?.align ?? defaultAlign(value);
          const step = align === 'right' ? -1 : 1;
          let last = c;
          while (width < needed && last + step >= 1 && last + step <= sheet.cols + 4) {
            const next = last + step;
            // What BLOCKS an overrun is something visible, not a cell object.
            // Giving an empty cell a fill, a border or bold creates one, and
            // testing for the object alone stopped the text dead at a neighbour
            // that draws nothing — the overrun looked broken for no reason on
            // screen.
            if (hasContent(getCell(sheet, r, next))) break;
            // A hidden column contributes no width, so stop rather than
            // walking silently past it.
            if (sheet.hiddenCols?.[next]) break;
            width += sizeAt(cols, next);
            last = next;
          }
          if (step < 0) {
            // Right-aligned text grows leftwards, so the box starts further left.
            into.push(
              <CellView
                key={`${r},${c}`}
                cell={cell}
                value={value}
                text={text}
                band={band}
                list={list}
                onChoose={(option) => store.chooseOption(r, c, option)}
                locked={locked}
                dbTitle={dbTitle}
                warn={store.cellWarning(sheet.id, r, c)}
                onColour={(hex) => store.setCellRaw(r, c, hex)}
                defaultOverflow={defaultOverflow}
                x={offsetAt(cols, last)}
                y={offsetAt(rows, r)}
                w={width}
                h={cellH(r)}
              />
            );
            continue;
          }
        }
      } else if (typeof value === 'number' && text !== '') {
        // Marked, not replaced: the number still reads as a number and the
        // tooltip carries it in full.
        overflowed = measureText(text, bold) + 10 > own;
      }

      into.push(
        <CellView
          key={`${r},${c}`}
          cell={cell}
          value={value}
          text={text}
          tooShort={overflowed}
          warn={store.cellWarning(sheet.id, r, c)}
          match={store.isMatch(sheet.id, r, c)}
          band={band}
          list={list}
          onChoose={(option) => store.chooseOption(r, c, option)}
          locked={locked}
          dbTitle={dbTitle}
          onColour={(hex) => store.setCellRaw(r, c, hex)}
          defaultOverflow={defaultOverflow}
          x={offsetAt(cols, c)}
          y={offsetAt(rows, r)}
          w={width}
          h={cellH(r)}
        />
      );
    }
  }

  /*
   * The sub-rows themselves: a label in the row gutter, a line under each, and
   * the values. Only the ones in view are drawn — a database of two thousand
   * objects is two thousand sub-rows.
   */
  for (const [r, { db, rows: subs }] of subRows) {
    if (!rowIndices.includes(r) || sheet.hiddenRows?.[r] || filtered.has(r)) continue;
    const top = offsetAt(rows, r) + cellH(r);
    // A frozen row's sub-rows are frozen with it — inside the row band, whose
    // backdrop would otherwise paint over them — and are always all in view.
    const inRow = r <= freezeRows;
    const rowLayer = inRow ? rowBand : cells;
    const colLayer = inRow ? cornerBand : colBand;
    const bandCls = inRow ? 'in-row' : '';
    const first = inRow ? 0 : Math.max(0, Math.floor((contentY - top) / SUB_ROW_H));
    const last = inRow ? subs.length - 1 : Math.min(subs.length - 1, Math.ceil((contentY + viewport.h - top) / SUB_ROW_H));
    const formats = db.columns.map((col) => columnFormat(db, col, data));
    for (let k = first; k <= last; k++) {
      const y = top + k * SUB_ROW_H;
      (inRow ? headerRowsFrozen : headerRows).push(
        <div
          key={`sub${r}.${k}`}
          className={`gt-rowhead is-sub${inRow ? ' is-frozen' : ''}`}
          style={{ top: y, height: SUB_ROW_H }}
          title={`Object ${k + 1} of the database on row ${r}. Sub-rows are outside the sheet's numbering, so rows below never move.`}
        >
          {r}.{k + 1}
        </div>
      );
      // The tint runs the width of the sheet, not just the database's columns,
      // so a sub-row reads as a row and not as a block of cells.
      rowLayer.push(<div key={`sg${r}.${k}`} className="gt-subrow-ground" style={{ top: y, width: cols.total, height: SUB_ROW_H }} />);
      (inRow ? rowBand : lines).push(<div key={`sh${r}.${k}`} className={`gt-hline is-sub ${bandCls}`} style={{ top: y, left: 0, width: cols.total }} />);
      // A frozen band covers the sheet's own lines, so it redraws them.
      if (freezeCols > 0) {
        colLayer.push(<div key={`scg${r}.${k}`} className="gt-subrow-ground" style={{ top: y, width: frozenW, height: SUB_ROW_H }} />);
        colLayer.push(<div key={`sch${r}.${k}`} className={`gt-hline is-sub ${inRow ? 'in-corner' : 'in-col'}`} style={{ top: y, left: 0, width: frozenW }} />);
      }
      subs[k]!.values.forEach((value, j) => {
        const c = db.anchor.col + j;
        if (sheet.hiddenCols?.[c]) return;
        const frozen = c <= freezeCols;
        (frozen ? colLayer : rowLayer).push(
          <div
            key={`sc${r}.${k},${c}`}
            className={`gt-cell gt-subcell gt-al-${defaultAlign(value)}${frozen ? ' in-col' : ''}${subMenu?.dbId === db.id && subMenu.sub === k ? ' is-picked' : ''}`}
            style={{ left: offsetAt(cols, c), top: y, width: sizeAt(cols, c), height: SUB_ROW_H }}
          >
            {formatValue(value, formats[j])}
          </div>
        );
      });
    }
  }

  const selX = offsetAt(cols, sel.c1);
  const selY = offsetAt(rows, sel.r1);
  const selW = offsetAt(cols, sel.c2) + sizeAt(cols, sel.c2) - selX;
  const selH = offsetAt(rows, sel.r2) + cellH(sel.r2) - selY;
  /*
   * The active-cell ring is its own box, separate from the selection
   * rectangle — ordinarily one cell, but when the active cell is a merge's
   * anchor it has to span the WHOLE merge too, or selecting a lone merged
   * cell shows a small ring sitting inside a bigger highlight box instead of
   * the one unified box Excel shows.
   */
  const activeMerge = mergeAt(sheet, sel.row, sel.col);
  const activeBox = activeMerge
    ? rangeRect(cols, rows, cellH, activeMerge.r1, activeMerge.c1, activeMerge.r2, activeMerge.c2)
    : { x: offsetAt(cols, sel.col), y: offsetAt(rows, sel.row), w: sizeAt(cols, sel.col), h: cellH(sel.row) };

  return (
    <div className="gt-grid">
      {dropSlot !== null && dragRef.current.kind === 'reorder' ? (
        <div
          className={`gt-band-caret is-${dragRef.current.axis}`}
          style={
            dragRef.current.axis === 'row'
              ? { top: HEADER_H + offsetAt(rows, dropSlot) - scroll.y, left: 0, right: 0 }
              : { left: HEADER_W + offsetAt(cols, dropSlot) - scroll.x, top: 0, bottom: 0 }
          }
        />
      ) : null}
      <div
        ref={bodyRef}
        className="gt-body"
        tabIndex={0}
        onScroll={onScroll}
        onPointerDown={onBodyPointerDown}
        onPointerMove={onBodyPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={onBodyDoubleClick}
        onContextMenu={onContextMenu}
        onKeyDown={onKeyDown}
        onCopy={onCopy}
        onCut={onCut}
        onPaste={onPaste}
      >
        <div className="gt-canvas" style={{ width: canvasW, height: canvasH }}>
          {/*
            * Sheet space. Inset by the gutter, so every coordinate inside is the
            * row/column offset itself — the same numbers as before the headers
            * moved in here. `z-index: 0` makes it a stacking context, so a
            * selection outline cannot climb over a header.
            */}
          <div className="gt-content" style={{ left: HEADER_W, top: HEADER_H, width: cols.total, height: rows.total }}>
          {lines}
          {cells}
          {/*
            * A frozen band is a zero-size sticky box holding cells at their real
            * sheet coordinates. Sticky at HEADER_W / HEADER_H is "against the far
            * edge of the gutter", so the band comes to rest exactly where the
            * header leaves off and the compositor holds it there.
            */}
          {colBand.length > 0 ? (
            <div className="gt-band in-col" style={{ left: HEADER_W }}>
              {colBand}
            </div>
          ) : null}
          {rowBand.length > 0 ? (
            <div className="gt-band in-row" style={{ top: HEADER_H }}>
              {rowBand}
            </div>
          ) : null}
          {cornerBand.length > 0 ? (
            <div className="gt-band in-corner" style={{ left: HEADER_W, top: HEADER_H }}>
              {cornerBand}
            </div>
          ) : null}
          {/* The selection rides with the band when it is inside one. */}
          <div
            className={`gt-pin-sel${pinSelX || pinSelY ? ' is-pinned' : ''}`}
            style={{ left: pinSelX ? HEADER_W : undefined, top: pinSelY ? HEADER_H : undefined }}
          >
            <div className="gt-selection" style={{ left: selX, top: selY, width: selW, height: selH }} />
            <div
              className="gt-active"
              style={{ left: activeBox.x, top: activeBox.y, width: activeBox.w, height: activeBox.h }}
            />
          </div>
          {/*
            * Every reference in the formula, boxed in its own colour.
            *
            * The same colour the text wears in the editor, which is what makes
            * a formula with five references readable at all: the eye pairs the
            * box with the token instead of counting commas. Excel and Sheets
            * both do it, and a formula editor without it feels broken.
            */}
          {editing
            ? colourRefs(editing.text)
                .filter((r) => (r.sheet ? r.sheet.toLowerCase() === sheet.name.toLowerCase() : editing.sheetId === sheet.id))
                // One box per reference, not per occurrence: `A1+A1*2` draws
                // the same box twice, and two 2px borders on the same pixels
                // are a heavier box for no reason.
                .filter((r, i, all) => all.findIndex((o) => o.key === r.key) === i)
                .map((r) => (
                  <div
                    key={r.key}
                    className="gt-ref-box"
                    style={{
                      left: offsetAt(cols, r.c1),
                      top: offsetAt(rows, r.r1),
                      width: offsetAt(cols, r.c2) + sizeAt(cols, r.c2) - offsetAt(cols, r.c1),
                      height: offsetAt(rows, r.r2) + cellH(r.r2) - offsetAt(rows, r.r1),
                      borderColor: r.colour,
                    }}
                  />
                ))
            : null}
          {editing?.pointing && editing.pointing.sheetId === sheet.id ? (
            <div
              className="gt-pointing"
              style={{
                left: offsetAt(cols, Math.min(editing.pointing.c1, editing.pointing.c2)),
                top: offsetAt(rows, Math.min(editing.pointing.r1, editing.pointing.r2)),
                width:
                  offsetAt(cols, Math.max(editing.pointing.c1, editing.pointing.c2)) +
                  sizeAt(cols, Math.max(editing.pointing.c1, editing.pointing.c2)) -
                  offsetAt(cols, Math.min(editing.pointing.c1, editing.pointing.c2)),
                height:
                  offsetAt(rows, Math.max(editing.pointing.r1, editing.pointing.r2)) +
                  cellH(Math.max(editing.pointing.r1, editing.pointing.r2)) -
                  offsetAt(rows, Math.min(editing.pointing.r1, editing.pointing.r2)),
              }}
            />
          ) : null}
          {fillPreview ? (
            <div
              className="gt-fill-preview"
              style={{
                left: offsetAt(cols, fillPreview.c1),
                top: offsetAt(rows, fillPreview.r1),
                width: offsetAt(cols, fillPreview.c2) + sizeAt(cols, fillPreview.c2) - offsetAt(cols, fillPreview.c1),
                height: offsetAt(rows, fillPreview.r2) + cellH(fillPreview.r2) - offsetAt(rows, fillPreview.r1),
              }}
            />
          ) : null}
          {editing ? null : (
            <div
              className="gt-fill-handle"
              title="Drag to fill"
              style={{ left: selX + selW - 4, top: selY + selH - 4 }}
              onPointerDown={onFillPointerDown}
              onDoubleClick={(e) => {
                // Excel's double-click-the-handle: fill down as far as the
                // column beside it runs.
                e.stopPropagation();
                const probe = sel.c1 > 1 ? sel.c1 - 1 : sel.c2 + 1;
                let end = sel.r2;
                while (end < sheet.rows && getCell(sheet, end + 1, probe)) end += 1;
                if (end > sel.r2) store.fillTo({ row: end, col: sel.c2 });
              }}
            />
          )}
          {Object.values(store.wb.charts).some((c) => c.sheetId === store.sel.sheetId) && (
            <Suspense fallback={null}>
              <ChartLayer />
            </Suspense>
          )}
          {/*
            * The editor stays mounted while pointing wanders to another sheet.
            * It cannot sit on its own cell there, so it holds the screen spot it
            * had at home — the formula being built has to stay readable, and in
            * the place the user is already looking.
            */}
          {editing ? (
            editing.sheetId === sheet.id ? (
              <CellEditor
                x={offsetAt(cols, editing.col)}
                y={offsetAt(rows, editing.row)}
                w={sizeAt(cols, editing.col)}
                h={cellH(editing.row)}
              />
            ) : (
              <CellEditor
                home={`${quoteSheetName(sheetById(store.wb, editing.sheetId)?.name ?? '?')}!${rowColToA1(editing.row, editing.col)}`}
                x={contentX + editorAnchor.current.x}
                y={contentY + editorAnchor.current.y}
                w={editorAnchor.current.w}
                h={editorAnchor.current.h}
              />
            )
          ) : null}
          </div>

          {/*
            * The headers, pinned by the same mechanism as the bands.
            *
            * Each is a zero-size sticky box — zero-size so the next one's static
            * position is still the canvas origin — holding an absolutely placed
            * strip. The frozen headers nest one more sticky inside their strip,
            * pinned on the OTHER axis, which is how a frozen column's letter
            * stays put both while the sheet scrolls down and while it scrolls
            * right. Nothing here reads the scroll position.
            */}
          <div className="gt-pin is-top">
            <div className="gt-colhead-strip" style={{ left: HEADER_W, width: canvasW - HEADER_W, height: HEADER_H }}>
              {headerCols}
              {headerColsFrozen.length > 0 ? (
                <div className="gt-pin is-across" style={{ left: HEADER_W }}>
                  {headerColsFrozen}
                  <div className="gt-freeze-line is-col" style={{ left: frozenW, height: HEADER_H }} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="gt-pin is-left">
            <div className="gt-rowhead-strip" style={{ top: HEADER_H, height: canvasH - HEADER_H, width: HEADER_W }}>
              {headerRows}
              {headerRowsFrozen.length > 0 ? (
                <div className="gt-pin is-across" style={{ top: HEADER_H }}>
                  {headerRowsFrozen}
                  <div className="gt-freeze-line is-row" style={{ top: frozenH, width: HEADER_W }} />
                </div>
              ) : null}
            </div>
          </div>
          <div className="gt-pin is-corner">
            <div
              className="gt-corner"
              style={{ width: HEADER_W, height: HEADER_H }}
              onPointerDown={() => store.selectRange(1, 1, sheet.rows, sheet.cols)}
            />
          </div>
        </div>
      </div>
      {menuAt ? <FormatMenu x={menuAt.x} y={menuAt.y} onClose={() => setMenuAt(null)} /> : null}
      {subMenu ? (
        <DbRowMenu
          x={subMenu.x}
          y={subMenu.y}
          dbId={subMenu.dbId}
          sub={subMenu.sub}
          label={subMenu.label}
          onClose={() => setSubMenu(null)}
        />
      ) : null}
    </div>
  );
}

/** 1..n, empty when n is 0. */
/** Something a reader can see: a value or a formula, not just formatting. */
function hasContent(cell: Cell | undefined): boolean {
  if (cell === undefined) return false;
  if (cell.f !== undefined) return true;
  return cell.v !== undefined && cell.v !== null && cell.v !== '';
}

function range(from: number, to: number): number[] {
  const out: number[] = [];
  for (let i = from; i <= to; i++) out.push(i);
  return out;
}

function union(a: number[], b: number[]): number[] {
  return [...new Set([...a, ...b])].sort((x, y) => x - y);
}

interface CellViewProps {
  cell: Cell | undefined;
  value: ReturnType<Engine['valueAt']>;
  warn: string | null;
  /** Already formatted by the caller, which also decides if it fits. */
  text: string;
  /** A number too wide for its column: shown as ### rather than truncated. */
  tooShort?: boolean;
  /** One of the current find results. */
  match?: boolean;
  /** Pinned in view: needs an opaque ground, or the sheet slides through it. */
  band?: 'none' | 'row' | 'col' | 'corner';
  /** The column's fixed list, when it has one. Draws the value as a chip. */
  list?: ColumnList;
  onChoose?: (option: string) => void;
  /** A chip on a locked cell (Areas) shows, but its click does not open the dropdown. */
  locked?: boolean;
  /** A database title: draws the ▾ that regroups and summarises the column. */
  dbTitle?: { dbId: string; index: number };
  onColour?: (hex: string) => void;
  /** What a cell with no overflow style of its own falls back to — see Grid()'s own. */
  defaultOverflow: 'overrun' | 'clip';
  x: number;
  y: number;
  w: number;
  h: number;
}

/** `#RGB` or `#RRGGBB` — a value that IS a colour, not one that describes it. */
const HEX_COLOUR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function CellView({ cell, value, warn, text, tooShort, match, band, list, onChoose, locked, dbTitle, defaultOverflow, x, y, w, h, onColour }: CellViewProps): JSX.Element {
  const isError = Engine.isError(value);
  const style = cell?.s;
  const align = style?.align ?? defaultAlign(value);
  // Only a literal: a formula that happens to return a hex is not a colour the
  // user can set, and offering a picker that overwrites their formula is worse
  // than offering nothing.
  const colour = cell?.f === undefined && typeof value === 'string' && HEX_COLOUR.test(value) ? value : null;
  return (
    <div
      className={`gt-cell${dbTitle ? ' is-db-title' : ''} gt-al-${align} gt-of-${cell?.s?.overflow ?? defaultOverflow}${isError ? ' is-error' : ''}${warn ? ' is-warn' : ''}${tooShort ? ' is-short' : ''}${match ? ' is-match' : ''}${band && band !== 'none' ? ` in-${band}` : ''}`}
      style={{
        left: x,
        top: y,
        width: w,
        height: h,
        fontWeight: style?.bold ? 600 : undefined,
        fontStyle: style?.italic ? 'italic' : undefined,
        background: style?.bg,
        color: style?.color,
      }}
      title={
        tooShort
          ? `${text} — the column is too narrow to show it all. Double-click the column edge to fit.`
          : isError
            ? String(value)
            : (warn ?? undefined)
      }
    >
      {list && cell?.f === undefined ? (
        <ChipCell list={list} text={text} onChoose={onChoose} locked={locked} />
      ) : null}
      {colour ? (
        // A cell holding a colour gets a swatch that opens the picker. It is how
        // a usage's colour is changed from the sheet — the value IS the colour,
        // so editing it should look like editing a colour.
        <label
          className="gt-colour-cell"
          style={{ background: colour }}
          title={`${colour} — click to change`}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <input
            type="color"
            value={colour}
            onChange={(e) => onColour?.(e.target.value.toUpperCase())}
          />
        </label>
      ) : null}
      {list && cell?.f === undefined ? null : dbTitle ? <span className="gt-db-title-text">{text}</span> : text}
      {dbTitle ? <DbTitleButton dbId={dbTitle.dbId} index={dbTitle.index} /> : null}
    </div>
  );
}

/**
 * A value from a fixed list, drawn as a chip.
 *
 * The chip is the useful half, not the input: a column of statuses reads as a
 * column of statuses at a glance, where the same words in plain text read as a
 * wall. A value the list does not have still shows — outlined rather than
 * filled — because a table over live project data that hides what the project
 * actually contains is worse than one that flags it.
 */
function ChipCell({
  list,
  text,
  onChoose,
  locked,
}: {
  list: ColumnList;
  text: string;
  onChoose?: (option: string) => void;
  locked?: boolean;
}): JSX.Element | null {
  const [at, setAt] = useState<AnchorRect | null>(null);
  const ref = useRef<HTMLButtonElement | null>(null);
  // Locked: the chip still SHOWS the value, as a plain pill — it just does
  // not open on click. A chip that opens a menu and then silently refuses
  // the pick still looks editable, which defeats the point of locking it.
  const open = (): void => {
    if (locked) return;
    setAt(rectOf(ref.current));
  };
  if (text.trim() === '') {
    if (locked) return null; // nothing to show, and nothing to pick
    return (
      <>
        <button
          ref={ref}
          className="gt-chip-empty"
          title="Choose a value"
          onPointerDown={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          ▾
        </button>
        {at ? <ChipMenu list={list} anchor={at} onPick={(o) => { setAt(null); onChoose?.(o); }} onClose={() => setAt(null)} /> : null}
      </>
    );
  }
  const index = optionIndex(list, text);
  const colour = colourFor(list, index);
  return (
    <>
      <button
        ref={ref}
        className={`gt-chip-cell${index < 0 ? ' is-off-list' : ''}${locked ? ' is-locked' : ''}`}
        style={index < 0 ? { color: colour, borderColor: colour } : { background: colour }}
        title={locked ? text : index < 0 ? `${text} — not one of this column's values` : `${text} — click to change`}
        onPointerDown={(e) => {
          e.stopPropagation();
          open();
        }}
      >
        {text}
      </button>
      {at ? <ChipMenu list={list} anchor={at} onPick={(o) => { setAt(null); onChoose?.(o); }} onClose={() => setAt(null)} /> : null}
    </>
  );
}

/**
 * Above this many options the menu gets a search box.
 *
 * A handful of statuses is a list you read; a project's worth of usages is one
 * you hunt through. Putting a box on every list would put one on TRUE/FALSE.
 */
const SEARCH_FROM = 8;

function ChipMenu({
  list,
  anchor,
  onPick,
  onClose,
}: {
  list: ColumnList;
  anchor: AnchorRect;
  onPick: (option: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  // Focused a frame late: the popover is hidden until it is placed, and a
  // hidden input refuses focus.
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const raf = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, []);
  // The original index travels with the option: the chip's colour is keyed by
  // position in the WHOLE list, so filtering must not recolour what is left.
  const shown = list.options
    .map((option, i) => ({ option, i }))
    .filter(({ option }) => q === '' || option.toLowerCase().includes(q));
  return (
    <Popover anchor={anchor} onClose={onClose} className="gt-menu-panel" swallowOutsidePress>
      {/* A list of the project's own values — layers, usages — always gets a
          search: its length is the project's, not something anyone chose. */}
      {list.options.length >= SEARCH_FROM || list.field ? (
        <input
          ref={searchRef}
          className="gt-input gt-chip-search"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            /*
             * The grid's key handler is an ancestor in the REACT tree, and a
             * portal's events bubble through that tree rather than the DOM one
             * — so without this, typing here also types into the cell
             * underneath. Escape is left alone: the popover's own listener is
             * on the document in the capture phase and closes the menu.
             */
            e.stopPropagation();
            if (e.key === 'Enter' && shown[0]) onPick(shown[0].option);
          }}
        />
      ) : null}
      <div className="gt-chip-options">
        {shown.map(({ option, i }) => (
          <ChipOption key={option} label={option} colour={colourFor(list, i)} onPick={() => onPick(option)} />
        ))}
        {shown.length === 0 ? <div className="gt-menu-note">No match</div> : null}
      </div>
      {/* Clearing is a value too: a status nobody has set yet is not "the first
          option", and a list with no way back to blank cannot say so. */}
      <div className="gt-menu-divider" />
      <ChipOption label="Clear" onPick={() => onPick('')} />
    </Popover>
  );
}

/**
 * One row of the chip menu.
 *
 * It answers to POINTERDOWN, not to click. A click needs the press and the
 * release on the same element, and the press re-renders the grid — the cell
 * changes, the sheet may be rewritten from the project under it — so the
 * element the mouse went down on is not always there for the release, and the
 * click never fires. The menu then looks like it is ignoring you.
 */
function ChipOption({
  label,
  colour,
  onPick,
}: {
  label: string;
  colour?: string;
  onPick: () => void;
}): JSX.Element {
  return (
    <div
      role="menuitem"
      tabIndex={0}
      className="gt-menu-row"
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onPick();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onPick();
      }}
    >
      <span className={`gt-chip-swatch${colour ? '' : ' is-none'}`} style={colour ? { background: colour } : undefined} />
      <span className="gt-menu-label">{label}</span>
    </div>
  );
}

/**
 * Borders as INSET box-shadows rather than CSS borders.
 *
 * A real border would take space inside the cell and shift its text by a pixel
 * the moment anyone drew one, which makes a bordered total row visibly jump out
 * of alignment with the column above it. An inset shadow paints in the same
 * place and costs no layout.
 */
/**
 * [0, total], with every exclusion range carved out — the pieces of a
 * gridline that are left to draw once a merge's interior has taken a bite out
 * of its middle. Overlapping exclusions are merged rather than assumed
 * disjoint, since nothing stops two merges in the same column/row from both
 * touching the same line.
 */
function splitExcluding(total: number, exclusions: [number, number][]): [number, number][] {
  if (exclusions.length === 0) return [[0, total]];
  const sorted = [...exclusions].sort((a, b) => a[0] - b[0]);
  const segments: [number, number][] = [];
  let cursor = 0;
  for (const [lo, hi] of sorted) {
    if (lo > cursor) segments.push([cursor, lo]);
    cursor = Math.max(cursor, hi);
  }
  if (cursor < total) segments.push([cursor, total]);
  return segments;
}

function borderShadow(borders: Partial<Record<BorderEdge, Border>> | undefined): string | undefined {
  if (!borders) return undefined;
  const parts: string[] = [];
  if (borders.top) parts.push(`inset 0 ${borders.top.w}px 0 0 ${borders.top.c}`);
  if (borders.bottom) parts.push(`inset 0 -${borders.bottom.w}px 0 0 ${borders.bottom.c}`);
  if (borders.left) parts.push(`inset ${borders.left.w}px 0 0 0 ${borders.left.c}`);
  if (borders.right) parts.push(`inset -${borders.right.w}px 0 0 0 ${borders.right.c}`);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/**
 * A shared edge's border can be set from BOTH sides of it, and each side
 * draws its own inset shadow independently — left alone, a line with a
 * border on both sides paints twice and reads twice as thick as either side
 * asked for.
 *
 * Resolved by splitting the difference rather than picking a winner:
 *   - A side with NO opposing border gets the FULL width it asked for,
 *     centred on the real boundary — grown into the neighbour by half its own
 *     width, since `.gt-cell-border` is its own div, free of the cell's own
 *     box. (`.gt-cell-border`'s z-index, 15, well above an ordinary cell's 1,
 *     is what makes the spilled-over half paint on top rather than under it.)
 *   - A side that DOES have an opposing border instead draws at HALF its own
 *     width, flush to its own edge, same as the opposite half. Two equal
 *     borders combine back to the one width either side asked for; two
 *     different ones meet in the middle each keeping half of what it set —
 *     nothing is silently dropped.
 *
 * `r2`/`c2` are a MERGE's far corner (equal to `r1`/`c1` for an ordinary
 * cell) — its bottom/right edge is checked against the cell just outside the
 * merge, not against its own anchor. `x`/`y`/`w`/`h` are that cell or merge's
 * own pixel box, before any growing.
 */
function cellBorderBox(
  sheet: Sheet,
  r1: number,
  c1: number,
  r2: number,
  c2: number,
  x: number,
  y: number,
  w: number,
  h: number
): { box: { x: number; y: number; w: number; h: number }; edges: Partial<Record<BorderEdge, Border>> } | null {
  const own = getCell(sheet, r1, c1)?.s?.borders;
  if (!own) return null;

  const aboveBottom = getCell(sheet, r1 - 1, c1)?.s?.borders?.bottom;
  const leftRight = getCell(sheet, r1, c1 - 1)?.s?.borders?.right;
  const belowTop = getCell(sheet, r2 + 1, c2)?.s?.borders?.top;
  const rightLeft = getCell(sheet, r2, c2 + 1)?.s?.borders?.left;

  const edges: Partial<Record<BorderEdge, Border>> = {};
  let padTop = 0;
  let padLeft = 0;
  let padBottom = 0;
  let padRight = 0;

  if (own.top) {
    if (aboveBottom) edges.top = { w: own.top.w / 2, c: own.top.c };
    else {
      edges.top = own.top;
      padTop = own.top.w / 2;
    }
  }
  if (own.left) {
    if (leftRight) edges.left = { w: own.left.w / 2, c: own.left.c };
    else {
      edges.left = own.left;
      padLeft = own.left.w / 2;
    }
  }
  if (own.bottom) {
    if (belowTop) edges.bottom = { w: own.bottom.w / 2, c: own.bottom.c };
    else {
      edges.bottom = own.bottom;
      padBottom = own.bottom.w / 2;
    }
  }
  if (own.right) {
    if (rightLeft) edges.right = { w: own.right.w / 2, c: own.right.c };
    else {
      edges.right = own.right;
      padRight = own.right.w / 2;
    }
  }

  if (Object.keys(edges).length === 0) return null;
  return {
    box: { x: x - padLeft, y: y - padTop, w: w + padLeft + padRight, h: h + padTop + padBottom },
    edges,
  };
}


/**
 * The disclosure on a database's row number: ▾ open, ▸ collapsed to the
 * database row alone. In the gutter because that is where the sub-row numbers
 * are — the thing it hides is right under it.
 */
function DbToggle({ db }: { db: Database }): JSX.Element {
  const n = store.dbObjectIds(db.id, null).length;
  return (
    <button
      className="gt-db-toggle"
      title={db.collapsed ? `Show the sub-rows (${n} ${n === 1 ? 'item' : 'items'})` : 'Collapse to the database row'}
      onPointerDown={(e) => {
        // Not a row selection: the header's own handler would start one.
        e.stopPropagation();
        e.preventDefault();
        store.updateDatabase(db.id, { collapsed: !db.collapsed }, db.collapsed ? 'Show sub-rows' : 'Hide sub-rows');
      }}
    >
      {db.collapsed ? '▸' : '▾'}
    </button>
  );
}
